import React, { useEffect, useState } from "react";
import { getApps, toggleAppFeature, getMessages, getSales } from "@/lib/api2";
import { getTrades, createTrade, closeTrade } from "@/lib/api";

// Renders the dashboard for the currently focused app, switched via voice or click.
export default function AppSurface({ appKey, refreshKey }) {
  const [app, setApp] = useState(null);

  useEffect(() => {
    if (!appKey) return;
    getApps().then(apps => setApp(apps.find(a => a.key === appKey)));
  }, [appKey, refreshKey]);

  if (!appKey || !app) {
    return (
      <div className="rc-panel p-6 h-full flex items-center justify-center" data-testid="app-surface-empty">
        <div className="text-center">
          <div className="rc-label mb-2">// APP SURFACE</div>
          <div className="text-xs text-[var(--rc-text-dim)] max-w-xs">Say <span className="rc-glow-text">"Raw Company, open SnapDog"</span> or pick an app from the list.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rc-panel p-4 h-full flex flex-col" data-testid={`app-surface-${app.key}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="rc-label">// {app.category.toUpperCase()}</div>
          <div className="font-display text-lg rc-glow-text mt-1">{app.name}</div>
          <div className="text-[10px] text-[var(--rc-text-dim)] mt-1">{app.description}</div>
        </div>
        <div className="text-right">
          <div className="rc-label text-[9px]">STATUS</div>
          <div className="font-display text-xs text-[var(--rc-success)] mt-1">OPERATIONAL</div>
        </div>
      </div>

      {/* Toggles */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        {Object.entries(app.toggles).map(([k, v]) => (
          <ToggleRow key={k} appKey={app.key} toggle={k} value={v} onChanged={() => getApps().then(apps => setApp(apps.find(a => a.key === app.key)))} />
        ))}
      </div>

      {/* App-specific content */}
      <div className="flex-1 overflow-y-auto">
        {app.key === "snapdog" && <MessagesView appKey="snapdog" />}
        {app.key === "clothing" && <SalesView />}
        {app.key === "trader" && <TraderView />}
        {app.key === "studio" && <StudioView app={app} />}
        {app.key === "web" && <WebView app={app} />}
      </div>
    </div>
  );
}

function ToggleRow({ appKey, toggle, value, onChanged }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const flip = async () => {
    const nv = !v;
    setV(nv);
    await toggleAppFeature(appKey, toggle, nv);
    onChanged?.();
  };
  return (
    <button onClick={flip} className={`border p-2 text-left transition-all ${v ? "border-[var(--rc-gold)] bg-[rgba(255,184,0,0.05)]" : "border-[var(--rc-gold-dim)] opacity-70"}`} data-testid={`toggle-${appKey}-${toggle}`}>
      <div className="flex items-center justify-between">
        <span className="rc-label text-[9px] truncate">{toggle.replace(/_/g, " ")}</span>
        <span className={`w-7 h-3 border ${v ? "border-[var(--rc-gold)] bg-[var(--rc-gold)]" : "border-[var(--rc-gold-dim)]"} relative`}>
          <span className={`absolute top-[-2px] w-3 h-4 bg-black border border-[var(--rc-gold)] transition-all ${v ? "right-[-2px]" : "left-[-2px]"}`} />
        </span>
      </div>
      <div className={`text-[10px] mt-1 ${v ? "text-[var(--rc-success)]" : "text-[var(--rc-text-dim)]"}`}>{v ? "ENABLED" : "DISABLED"}</div>
    </button>
  );
}

function MessagesView({ appKey }) {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => { getMessages(appKey).then(setMsgs); }, [appKey]);
  return (
    <div className="space-y-2">
      <div className="rc-label">// INBOX · {msgs.filter(m => m.unread).length} UNREAD</div>
      {msgs.map(m => (
        <div key={m.id} className={`border p-2 ${m.unread ? "border-[var(--rc-gold)]" : "border-[var(--rc-gold-dim)] opacity-60"}`} data-testid={`message-${m.id}`}>
          <div className="flex items-center justify-between">
            <span className="font-display text-xs">{m.sender}</span>
            <span className={`text-[9px] tracking-widest ${m.priority === "urgent" ? "text-[var(--rc-danger)]" : "text-[var(--rc-text-dim)]"}`}>{m.priority.toUpperCase()}</span>
          </div>
          <div className="text-xs text-[var(--rc-text)] mt-1">{m.preview}</div>
        </div>
      ))}
    </div>
  );
}

function SalesView() {
  const [data, setData] = useState(null);
  useEffect(() => { getSales("clothing", 24).then(setData); }, []);
  if (!data) return <div className="rc-label rc-blink">LOADING</div>;
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div className="border border-[var(--rc-gold-dim)] p-2">
          <div className="rc-label text-[9px]">ITEMS SOLD / 24H</div>
          <div className="font-display rc-glow-text mt-1 tabular-nums">{data.qty}</div>
        </div>
        <div className="border border-[var(--rc-gold-dim)] p-2">
          <div className="rc-label text-[9px]">REVENUE / 24H</div>
          <div className="font-display rc-glow-text mt-1 tabular-nums">£{data.revenue.toLocaleString()}</div>
        </div>
      </div>
      <div className="rc-label mt-2">// LATEST SALES</div>
      {data.items.slice(0, 12).map(s => (
        <div key={s.id} className="flex items-center justify-between text-xs border-b border-[var(--rc-gold-dim)] py-1">
          <span>{s.item} <span className="rc-label text-[9px] ml-1">×{s.qty}</span></span>
          <span className="tabular-nums">£{s.revenue.toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
}

function TraderView() {
  const [trades, setTrades] = useState([]);
  const load = () => getTrades().then(setTrades);
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-2">
      <div className="rc-label">// OPEN POSITIONS</div>
      {trades.filter(t => t.status === "open").map(t => {
        const profit = t.pnl >= 0;
        return (
          <div key={t.id} className="border border-[var(--rc-gold-dim)] p-2">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-display text-sm">{t.symbol}</span>
                <span className={`ml-2 text-[10px] tracking-widest ${t.side === "buy" ? "text-[var(--rc-success)]" : "text-[var(--rc-danger)]"}`}>{t.side.toUpperCase()}</span>
              </div>
              <span className={`font-display text-sm tabular-nums ${profit ? "text-[var(--rc-success)]" : "text-[var(--rc-danger)]"}`}>{profit ? "+" : ""}£{t.pnl?.toFixed(2)}</span>
            </div>
            <div className="rc-label text-[9px] mt-1">CAP £{t.capital} · ENTRY {t.entry_price}</div>
            <button className="rc-btn mt-2 !text-[9px] !py-1 !px-2 w-full" onClick={() => closeTrade(t.id).then(load)} data-testid={`close-${t.id}`}>CLOSE</button>
          </div>
        );
      })}
      {trades.filter(t => t.status === "open").length === 0 && <div className="rc-label opacity-50 text-center mt-4">NO OPEN POSITIONS</div>}
    </div>
  );
}

function StudioView({ app }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div className="border border-[var(--rc-gold-dim)] p-2">
          <div className="rc-label text-[9px]">JOBS QUEUED</div>
          <div className="font-display rc-glow-text mt-1">{app.metrics.jobs_queued}</div>
        </div>
        <div className="border border-[var(--rc-gold-dim)] p-2">
          <div className="rc-label text-[9px]">GPU UTILISATION</div>
          <div className="font-display rc-glow-text mt-1">{app.metrics.gpu_util}%</div>
        </div>
      </div>
    </div>
  );
}

function WebView({ app }) {
  return (
    <div className="space-y-2">
      <div className="border border-[var(--rc-gold-dim)] p-2">
        <div className="rc-label text-[9px]">UPTIME / 30D</div>
        <div className="font-display rc-glow-text mt-1">{app.metrics.uptime_pct}%</div>
      </div>
    </div>
  );
}
